import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Copy,
  Check,
  AtSign,
  UserPlus,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function MfaCodeField({ value, onChange, testid = "account-mfa-code-input" }) {
  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="\d{6}"
      maxLength={6}
      required
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      className="h-12 rounded-md border-zinc-300 font-mono tracking-[0.4em] text-center max-w-[200px]"
      placeholder="000000"
      data-testid={testid}
    />
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const mfaEnabled = Boolean(user && user.mfa_enabled);

  // Change password
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwMfaCode, setPwMfaCode] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  // Change email
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailMfaCode, setEmailMfaCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  // MFA setup
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  // Create another admin
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminName, setNewAdminName] = useState("");
  const [newAdminUseMfa, setNewAdminUseMfa] = useState(true);
  const [newAdminBusy, setNewAdminBusy] = useState(false);
  const [createdAdmin, setCreatedAdmin] = useState(null);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (mfaEnabled && pwMfaCode.length !== 6) {
      toast.error("Enter your 6-digit authenticator code");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        mfa_code: mfaEnabled ? pwMfaCode : undefined,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
      setPwMfaCode("");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setPwBusy(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (mfaEnabled && emailMfaCode.length !== 6) {
      toast.error("Enter your 6-digit authenticator code");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post("/auth/change-email", {
        current_password: emailPw,
        new_email: newEmail.trim(),
        mfa_code: mfaEnabled ? emailMfaCode : undefined,
      });
      toast.success("Email updated");
      setNewEmail("");
      setEmailPw("");
      setEmailMfaCode("");
      await refresh();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setEmailBusy(false);
    }
  };

  const startMfaSetup = async () => {
    setMfaBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
      setEnableCode("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setMfaBusy(false);
    }
  };

  const handleEnableMfa = async (e) => {
    e.preventDefault();
    if (enableCode.length !== 6) return;
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code: enableCode });
      toast.success("Two-factor authentication enabled");
      setSetupData(null);
      setEnableCode("");
      await refresh();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setMfaBusy(false);
    }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    if (disableCode.length !== 6) {
      toast.error("Enter your 6-digit authenticator code");
      return;
    }
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePw, mfa_code: disableCode });
      toast.success("Two-factor authentication disabled");
      setDisablePw("");
      setDisableCode("");
      await refresh();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setMfaBusy(false);
    }
  };

  const copySecret = async () => {
    if (!setupData) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (newAdminPassword.length < 8) {
      toast.error("New admin password must be at least 8 characters");
      return;
    }
    setNewAdminBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        email: newAdminEmail.trim(),
        password: newAdminPassword,
        name: newAdminName.trim() || undefined,
        generate_mfa: newAdminUseMfa,
      });
      setCreatedAdmin(data);
      setNewAdminEmail("");
      setNewAdminPassword("");
      setNewAdminName("");
      toast.success("Admin created");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setNewAdminBusy(false);
    }
  };

  const copyNewAdminSecret = async () => {
    if (!createdAdmin?.mfa_setup?.secret) return;
    try {
      await navigator.clipboard.writeText(createdAdmin.mfa_setup.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="text-sm text-zinc-700 hover:text-zinc-900 inline-flex items-center"
            data-testid="account-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500">
            Account
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500 mb-3">
            Signed in as
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900 leading-[0.95]"
            style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
            data-testid="account-heading"
          >
            {user.email}
          </h1>
          {mfaEnabled ? (
            <p className="mt-3 text-sm text-emerald-700 inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Two-factor authentication is on.
              Password & email changes require a code.
            </p>
          ) : (
            <p className="mt-3 text-sm text-zinc-500 inline-flex items-center gap-2">
              <ShieldOff className="w-4 h-4" /> Two-factor authentication is off.
            </p>
          )}
        </div>

        {/* ---- Change email ---- */}
        <section className="border border-zinc-200 rounded-lg p-6 sm:p-8" data-testid="account-email-section">
          <div className="flex items-center gap-2 mb-6">
            <AtSign className="w-4 h-4 text-zinc-700" />
            <h2 className="text-xl font-bold tracking-tight text-zinc-900">Change email</h2>
            {mfaEnabled && (
              <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">
                MFA required
              </span>
            )}
          </div>
          {!mfaEnabled ? (
            <p className="text-sm text-zinc-600">
              Enable two-factor authentication below to change your email address.
            </p>
          ) : (
            <form onSubmit={handleChangeEmail} className="space-y-5" data-testid="account-email-form">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  New email
                </Label>
                <Input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-12 rounded-md border-zinc-300"
                  data-testid="account-new-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Current password
                </Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={emailPw}
                  onChange={(e) => setEmailPw(e.target.value)}
                  className="h-12 rounded-md border-zinc-300"
                  data-testid="account-email-pw-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Authenticator code
                </Label>
                <MfaCodeField
                  value={emailMfaCode}
                  onChange={setEmailMfaCode}
                  testid="account-email-mfa-input"
                />
              </div>
              <Button
                type="submit"
                disabled={emailBusy}
                className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="account-change-email-btn"
              >
                {emailBusy ? "Saving…" : "Update email"}
              </Button>
            </form>
          )}
        </section>

        {/* ---- Change password ---- */}
        <section className="border border-zinc-200 rounded-lg p-6 sm:p-8" data-testid="account-password-section">
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="w-4 h-4 text-zinc-700" />
            <h2 className="text-xl font-bold tracking-tight text-zinc-900">Change password</h2>
            {mfaEnabled && (
              <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">
                MFA required
              </span>
            )}
          </div>
          <form onSubmit={handleChangePassword} className="space-y-5" data-testid="account-password-form">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Current password
              </Label>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="h-12 rounded-md border-zinc-300"
                data-testid="account-current-pw-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                New password
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="h-12 rounded-md border-zinc-300"
                data-testid="account-new-pw-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Confirm new password
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12 rounded-md border-zinc-300"
                data-testid="account-confirm-pw-input"
              />
            </div>
            {mfaEnabled && (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Authenticator code
                </Label>
                <MfaCodeField
                  value={pwMfaCode}
                  onChange={setPwMfaCode}
                  testid="account-pw-mfa-input"
                />
              </div>
            )}
            <Button
              type="submit"
              disabled={pwBusy}
              className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="account-change-pw-btn"
            >
              {pwBusy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </section>

        {/* ---- MFA ---- */}
        <section className="border border-zinc-200 rounded-lg p-6 sm:p-8" data-testid="account-mfa-section">
          <div className="flex items-center gap-2 mb-2">
            {mfaEnabled ? (
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            ) : (
              <ShieldOff className="w-4 h-4 text-zinc-700" />
            )}
            <h2 className="text-xl font-bold tracking-tight text-zinc-900">
              Two-factor authentication
            </h2>
            {mfaEnabled && (
              <span
                className="ml-auto text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                data-testid="account-mfa-status-on"
              >
                On
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-600 mb-6">
            Use an authenticator app like 1Password, Google Authenticator, Authy, or Bitwarden.
          </p>

          {!mfaEnabled && !setupData && (
            <Button
              onClick={startMfaSetup}
              disabled={mfaBusy}
              className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="account-mfa-start-btn"
            >
              {mfaBusy ? "Preparing…" : "Set up authenticator"}
            </Button>
          )}

          {!mfaEnabled && setupData && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="p-3 bg-white border border-zinc-200 rounded-md" data-testid="account-mfa-qr">
                  <QRCodeSVG value={setupData.otpauth_url} size={172} level="M" />
                </div>
                <div className="flex-1 space-y-3 min-w-0">
                  <p className="text-sm text-zinc-700">
                    Scan the QR code with your authenticator app, or paste this secret manually.
                  </p>
                  <div className="flex items-center gap-2">
                    <code
                      className="flex-1 font-mono text-xs sm:text-sm bg-zinc-100 rounded-md px-3 py-2 truncate"
                      data-testid="account-mfa-secret"
                    >
                      {setupData.secret}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 rounded-md border-zinc-300"
                      onClick={copySecret}
                      data-testid="account-mfa-copy-btn"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleEnableMfa} className="space-y-3" data-testid="account-mfa-enable-form">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Enter the 6-digit code your app shows
                </Label>
                <div className="flex items-center gap-2">
                  <MfaCodeField
                    value={enableCode}
                    onChange={setEnableCode}
                    testid="account-mfa-enable-code"
                  />
                  <Button
                    type="submit"
                    disabled={mfaBusy || enableCode.length !== 6}
                    className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="account-mfa-enable-btn"
                  >
                    Verify & enable
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12"
                    onClick={() => {
                      setSetupData(null);
                      setEnableCode("");
                    }}
                    data-testid="account-mfa-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {mfaEnabled && (
            <form onSubmit={handleDisableMfa} className="space-y-3" data-testid="account-mfa-disable-form">
              <p className="text-sm text-zinc-700">
                Two-factor authentication is active. To disable, confirm your password
                <strong> and </strong>your current code.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="password"
                  required
                  placeholder="Your password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  className="h-12 rounded-md border-zinc-300"
                  data-testid="account-mfa-disable-pw"
                />
                <MfaCodeField
                  value={disableCode}
                  onChange={setDisableCode}
                  testid="account-mfa-disable-code"
                />
              </div>
              <Button
                type="submit"
                disabled={mfaBusy || !disablePw || disableCode.length !== 6}
                className="h-12 rounded-md bg-red-600 hover:bg-red-700 text-white"
                data-testid="account-mfa-disable-btn"
              >
                Disable two-factor
              </Button>
            </form>
          )}
        </section>

        {/* ---------- Create another admin ---------- */}
        <section className="border border-zinc-200 rounded-lg p-6 space-y-4" data-testid="account-create-admin-section">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-zinc-700" />
            <h2 className="text-lg font-bold tracking-tight">Create another admin</h2>
          </div>
          <p className="text-sm text-zinc-600">
            Add a second admin account as a backup. If you ever lose access to this account, you can sign in with the new one. Each admin manages their own MFA from their own Account page.
          </p>

          {!createdAdmin && (
            <form onSubmit={handleCreateAdmin} className="space-y-3" data-testid="account-create-admin-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                  <Input
                    type="email"
                    required
                    placeholder="backup-admin@example.com"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    className="h-12 rounded-md border-zinc-300"
                    data-testid="account-new-admin-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Display name (optional)</Label>
                  <Input
                    type="text"
                    placeholder="Backup Admin"
                    value={newAdminName}
                    onChange={(e) => setNewAdminName(e.target.value)}
                    className="h-12 rounded-md border-zinc-300"
                    data-testid="account-new-admin-name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Password (min 8 chars)</Label>
                <Input
                  type="password"
                  required
                  minLength={8}
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  className="h-12 rounded-md border-zinc-300"
                  data-testid="account-new-admin-password"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-zinc-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newAdminUseMfa}
                  onChange={(e) => setNewAdminUseMfa(e.target.checked)}
                  className="h-4 w-4 rounded border-zinc-400"
                  data-testid="account-new-admin-mfa-toggle"
                />
                Set up two-factor authentication for this admin (you'll get a QR to scan now)
              </label>
              <Button
                type="submit"
                disabled={newAdminBusy || !newAdminEmail.trim() || newAdminPassword.length < 8}
                className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="account-new-admin-submit"
              >
                {newAdminBusy ? "Creating…" : "Create admin account"}
              </Button>
            </form>
          )}

          {createdAdmin && (
            <div className="space-y-4 rounded-md border border-emerald-200 bg-emerald-50/60 p-4" data-testid="account-new-admin-result">
              <div className="text-sm">
                <div className="font-semibold text-emerald-900">Admin created ✓</div>
                <div className="text-emerald-800 mt-1">
                  Email: <span className="font-mono">{createdAdmin.user.email}</span>
                </div>
              </div>

              {createdAdmin.mfa_setup && (
                <div className="space-y-3">
                  <p className="text-sm text-zinc-700">
                    <strong>Important:</strong> this is the only time the MFA secret will be shown. Have the new admin scan the QR code below with their authenticator app (Google Authenticator, 1Password, Authy, etc.) right now — or copy the secret manually.
                  </p>
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className="bg-white p-3 rounded-md border border-zinc-200" data-testid="account-new-admin-qr">
                      <QRCodeSVG value={createdAdmin.mfa_setup.otpauth_url} size={172} level="M" />
                    </div>
                    <div className="flex-1 space-y-2 w-full">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Secret (manual entry)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={createdAdmin.mfa_setup.secret}
                          className="h-11 font-mono text-xs rounded-md border-zinc-300"
                          data-testid="account-new-admin-secret"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={copyNewAdminSecret}
                          className="h-11 rounded-md border-zinc-300"
                          data-testid="account-new-admin-copy-secret"
                        >
                          {secretCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-zinc-500 break-all">
                        otpauth URL: <span className="font-mono">{createdAdmin.mfa_setup.otpauth_url}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={() => setCreatedAdmin(null)}
                className="h-10 rounded-md border-zinc-300"
                data-testid="account-new-admin-done"
              >
                Done — create another
              </Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
